import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import RadioBtn from '../../elements/RadioBtn';
import { LabelLarge, Input, Form as FormElement, PrimaryButton, InputErrorMessage } from '../../elements';
import { addAdditionalInsured } from '../../actions/application';
import AddressInput from './AddressInput';
import { validateEmailRegex } from '../../helpers/inputValidation/validateEmail';
import { INPUT_ERROR_MSG } from '../../constants';
import { selectApplication } from '../../selectors/application';
import { PolicyType } from '../../types/enums';
import { useFeatureFlags } from '../../toggle_tools/featureFlagTools';

const StyledPrimaryButton = styled(PrimaryButton)`
  margin: 24px 0 0;
`;

export type AdditionalInsuredPolicyType = PolicyType | 'Both' | undefined;

type AdditionalInsuredParams = {
  insured?: AdditionalInsured;
  policyType?: AdditionalInsuredPolicyType;
};
interface PropTypes {
  addAdditionalInsured: ({ insured, policyType }: AdditionalInsuredParams) => void;
  applicationTypes?: PolicyType[];
  policyType?: AdditionalInsuredPolicyType;
  setPolicyType: (policyType: AdditionalInsuredPolicyType) => void;
}

const policyTypes = {
  [PolicyType.BOP]: 'Business Owner Policy',
  [PolicyType.GL]: 'General Liability',
  [PolicyType.PL]: 'Professional Liability',
  Both: 'Both Policies',
};

const AdditionalInsuredsForm = ({ addAdditionalInsured, applicationTypes, policyType, setPolicyType }: PropTypes) => {
  const { showPlv2 } = useFeatureFlags();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [addInsAddress, setAddInsAddress] = useState({ street: '', city: '', zip: '', state: '' });

  const handleEmailOnBlur = () => setIsEmailValid(validateEmailRegex(email));

  const submitAdditionalInsured = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { street, city, zip, state } = addInsAddress;
    addAdditionalInsured({ insured: { name, street, city, zip, state, email }, policyType });
    setName('');
    setEmail('');
    setAddInsAddress({ street: '', city: '', zip: '', state: '' });
    setPolicyType(undefined);
  };

  return (
    <>
      <LabelLarge>Full Name of Individual or Legal Entity</LabelLarge>
      <Input
        width="full"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-cy="add-ins-name"
        aria-label="Name of additional insured"
        marginbottom="smaller"
      />
      <LabelLarge>Email</LabelLarge>
      <StyledInput
        width="full"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={handleEmailOnBlur}
        hasError={!isEmailValid}
        data-cy="add-ins-email"
        aria-label="Email of additional insured"
        marginbottom="smaller"
      />
      {!isEmailValid && (
        <StyledInputErrorMessage>{!isEmailValid && INPUT_ERROR_MSG.INVALID_EMAIL}</StyledInputErrorMessage>
      )}
      {showPlv2 && applicationTypes && applicationTypes.length > 1 && (
        <div>
          <RadioContainer data-cy="policy-type" data-testid="policy-type">
            <RadioText data-cy="policy-type-text">
              Which policy do you want to add this additional insured to?
            </RadioText>
            {applicationTypes?.sort().map((type) => (
              <RadioButton
                key={type}
                id={`policy-type-${type.toLowerCase()}`}
                dataCy={`policy-type-${type.toLowerCase()}`}
                labelText={policyTypes[type]}
                isChecked={policyType === type}
                onChange={() => setPolicyType(type)}
                name={`policy-type-${type.toLowerCase()}`}
                style={{ marginBottom: '0' }}
              />
            ))}
            <RadioButton
              id="policy-type-both"
              dataCy="policy-type-both"
              labelText="Both Policies"
              isChecked={policyType === 'Both'}
              onChange={() => setPolicyType('Both')}
              name="policy-type-both"
              style={{ marginBottom: '0' }}
            />
          </RadioContainer>
        </div>
      )}
      <AddressInput addInsAddress={addInsAddress} setAddInsAddress={setAddInsAddress} />
      <StyledPrimaryButton
        type="button"
        onClick={submitAdditionalInsured}
        disabled={
          showPlv2 && applicationTypes && applicationTypes?.length > 1
            ? !Boolean(addInsAddress.street && name && validateEmailRegex(email) && policyType)
            : !Boolean(addInsAddress.street && name && validateEmailRegex(email))
        }
        data-cy="add-addl-ins"
        aria-label="Add additional insured"
        small
      >
        Add person
      </StyledPrimaryButton>
    </>
  );
};

const Form = styled(FormElement)`
  margin: 0;
  display: inherit;
  margin-bottom: 25px;
`;

const StyledInput = styled(Input)`
  margin-bottom: 16px;
`;

const StyledInputErrorMessage = styled(InputErrorMessage)`
  height: 16px;
  margin: 0 0 24px;
`;

const RadioContainer = styled.fieldset`
  border: none;
  margin: 16px 0;
  padding: 0;
  display: flex;
  gap: 30px;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
`;

const RadioText = styled.legend`
  font-size: 16px;
  margin-bottom: 16px;
`;

const RadioButton = styled(RadioBtn)`
  margin-bottom: 0;
`;

const mapStateToProps = (state: ReduxState) => ({
  applicationTypes: selectApplication(state).applicationTypes,
});

const mapDispatchToProps = (dispatch: any) => ({
  addAdditionalInsured: (data: AdditionalInsuredParams) => dispatch(addAdditionalInsured(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalInsuredsForm);
